import React, { useEffect, useState } from "react";
import { usePageMetadata } from "../context/PageMetadataContext";
import { Table, Card, Row, Col, Badge, Spinner, Button, Form, InputGroup } from "react-bootstrap";
import { FiTrendingUp, FiSearch, FiChevronLeft, FiChevronRight, FiExternalLink, FiArrowRight } from "react-icons/fi";
import { useLanguage } from "../context/LanguageContext";

// Define types for the trades API data
interface TradePrice {
  n?: string;
  d?: string;
}

interface Trade {
  id?: string;
  paging_token?: string;
  ledger_close_time?: string;
  trade_type?: string;
  base_offer_id?: string;
  base_account?: string;
  base_amount?: string;
  base_asset_type?: string;
  base_asset_code?: string;
  base_asset_issuer?: string;
  counter_offer_id?: string;
  counter_account?: string;
  counter_amount?: string;
  counter_asset_type?: string;
  counter_asset_code?: string;
  counter_asset_issuer?: string;
  base_is_seller?: boolean;
  price?: TradePrice;
  _links?: {
    self?: { href?: string };
    base?: { href?: string };
    counter?: { href?: string };
    operation?: { href?: string };
  };
}

interface TradesApiResponse {
  _links: {
    self: { href: string };
    next?: { href: string };
    prev?: { href: string };
  };
  _embedded: {
    records: Trade[];
  };
}

const Trades: React.FC = () => {
  const [tradesData, setTradesData] = useState<TradesApiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filteredTrades, setFilteredTrades] = useState<Trade[]>([]);
  const [exchangeRates, setExchangeRates] = useState<{[key: string]: number}>({});
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  const { t, language } = useLanguage();

  useEffect(() => {
    setHeading(t('trades.heading'));
    setTitle(t('trades.title'));
    setDescription(t('trades.description'));
  }, [setHeading, setTitle, setDescription, t, language]);
  
  useEffect(() => {
    fetchTrades();
  }, []);

  useEffect(() => {
    if (tradesData?._embedded?.records) {
      const filtered = tradesData._embedded.records.filter(trade =>
        trade.base_asset_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        trade.counter_asset_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        trade.base_account?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        trade.counter_account?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredTrades(filtered);
      
      // Calculate exchange rates from PI to other assets
      calculateExchangeRates(tradesData._embedded.records);
    }
  }, [tradesData, searchQuery]);

  const calculateExchangeRates = (trades: Trade[]) => {
    const rates: {[key: string]: number} = {};
    
    // Process recent trades to get latest exchange rates
    trades.slice(0, 20).forEach(trade => {
      if (!trade.price || !trade.price.n || !trade.price.d) return;
      
      const baseAsset = formatAsset(trade.base_asset_type, trade.base_asset_code);
      const counterAsset = formatAsset(trade.counter_asset_type, trade.counter_asset_code);
      const n = parseFloat(trade.price.n);
      const d = parseFloat(trade.price.d);
      
      if (isNaN(n) || isNaN(d) || d === 0) return;
      
      const price = n / d;
      
      // If base is PI, counter asset rate = price
      if (baseAsset === 'PI' && counterAsset !== 'PI' && !isNaN(price)) {
        rates[counterAsset] = price;
      }
      // If counter is PI, base asset rate = 1/price  
      else if (counterAsset === 'PI' && baseAsset !== 'PI' && !isNaN(price) && price !== 0) {
        rates[baseAsset] = 1 / price;
      }
    });
    
    setExchangeRates(rates);
  };

  const fetchTrades = async (url?: string) => {
    try {
      setLoading(true);
      const apiUrl = url || 'https://api.testnet.minepi.com/trades?limit=50&order=desc';
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      setTradesData(data);
      setLoading(false);
    } catch (err) {
      setError(`Failed to fetch trades data: ${err instanceof Error ? err.message : String(err)}`);
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (tradesData?._links?.next) {
      fetchTrades(tradesData._links.next.href);
    }
  };

  const handlePrevPage = () => {
    if (tradesData?._links?.prev) {
      fetchTrades(tradesData._links.prev.href);
    }
  };

  const formatNumber = (num?: string): string => {
    if (!num || num === '') return '0';
    const number = parseFloat(num);
    if (isNaN(number)) return '0';
    return number.toLocaleString(language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 7
    });
  };

  const formatPrice = (price?: TradePrice): string => {
    if (!price || !price.n || !price.d) return '0';
    const n = parseFloat(price.n);
    const d = parseFloat(price.d);
    if (isNaN(n) || isNaN(d) || d === 0) return '0';
    const priceValue = n / d;
    return priceValue.toLocaleString(language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 7
    });
  };

  const formatAsset = (assetType?: string, assetCode?: string): string => {
    if (!assetType) return 'Unknown';
    if (assetType === 'native') return 'PI';
    return assetCode || 'Unknown';
  };

  const formatDateTime = (dateTime?: string): string => {
    if (!dateTime) return 'Unknown';
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString(language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getTradeTypeColor = (tradeType?: string): string => {
    if (!tradeType) return 'secondary';
    switch (tradeType) {
      case 'orderbook':
        return 'primary';
      case 'liquidity_pool':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const getTradeTypeLabel = (tradeType?: string): string => {
    if (!tradeType) return 'Unknown';
    switch (tradeType) {
      case 'orderbook':
        return 'Order Book';
      case 'liquidity_pool':
        return 'Liquidity Pool';
      default:
        return tradeType;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  if (!tradesData) {
    return <div className="alert alert-warning">No trades data available</div>;
  }

  return (
    <div className="trades-page">
      {/* Header with Search */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between flex-wrap">
                <div className="d-flex align-items-center mb-2 mb-md-0">
                  <div className="rounded-circle p-2 bg-primary bg-opacity-10 me-3">
                    {FiTrendingUp({size:24,className:'text-primary'})}
                  </div>
                  <div>
                    <h4 className="mb-1">{t('trades.total_trades')}</h4>
                    <p className="text-danger mb-0 small fw-medium">
                      {t('trades.testnet_warning')}
                    </p>
                    
                  </div>
                </div>
                <div className="search-container" style={{ minWidth: '300px' }}>
                  <InputGroup>
                    <InputGroup.Text>
                      {FiSearch({size:16})}
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder={t('trades.search_placeholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </InputGroup>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Trades Table */}
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t('trades.trades_list')}</h5>
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  disabled={!tradesData._links.prev}
                  onClick={handlePrevPage}
                >
                  {FiChevronLeft({size:16,className:'me-1'})}
                  {t('trades.previous')}
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  disabled={!tradesData._links.next}
                  onClick={handleNextPage}
                >
                  {t('trades.next')}
                  {FiChevronRight({size:16,className:'ms-1'})}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>{t('trades.time')}</th>
                      <th>{t('trades.type')}</th>
                      <th>{t('trades.summary')}</th>
                      <th className="text-end">{t('trades.amount')}</th>
                      <th className="text-end">{t('trades.price')}</th>
                      <th>{t('trades.accounts')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTrades.map((trade, index) => (
                      <tr key={trade.id}>
                        <td>
                          <small className="text-muted">
                            {formatDateTime(trade.ledger_close_time)}
                          </small>
                        </td>
                        <td>
                          <Badge bg={getTradeTypeColor(trade.trade_type)}>
                            {getTradeTypeLabel(trade.trade_type)}
                          </Badge>
                        </td>
                        <td>
                          <div className="trade-summary">
                            <div className="d-flex align-items-center mb-1">
                              <span className="fw-medium text-primary">
                                {formatAsset(trade.base_asset_type, trade.base_asset_code)}
                              </span>
                              {FiArrowRight({size:14,className:'mx-1 text-muted'})}
                              <span className="fw-medium text-success">
                                {formatAsset(trade.counter_asset_type, trade.counter_asset_code)}
                              </span>
                            </div>
                            <small className="text-muted">
                              {(() => {
                                const baseAsset = formatAsset(trade.base_asset_type, trade.base_asset_code);
                                const counterAsset = formatAsset(trade.counter_asset_type, trade.counter_asset_code);
                                const baseAmount = trade.base_amount ? parseFloat(trade.base_amount) : 0;
                                const counterAmount = trade.counter_amount ? parseFloat(trade.counter_amount) : 0;
                                
                                // Check if amounts are valid numbers
                                if (isNaN(baseAmount) || isNaN(counterAmount)) {
                                  return `${baseAsset} ⇄ ${counterAsset}`;
                                }
                                
                                return `${baseAmount.toLocaleString(language, {maximumFractionDigits: 7})} ${baseAsset} ==> ${counterAmount.toLocaleString(language, {maximumFractionDigits: 7})} ${counterAsset}`;
                              })()}
                            </small>
                          </div>
                        </td>
                        <td className="text-end">
                          <div>
                            <div className="fw-medium">
                              {formatNumber(trade.base_amount)} {formatAsset(trade.base_asset_type, trade.base_asset_code)}
                            </div>
                            <small className="text-muted">
                              {formatNumber(trade.counter_amount)} {formatAsset(trade.counter_asset_type, trade.counter_asset_code)}
                            </small>
                          </div>
                        </td>
                        <td className="text-end">
                          <span className="fw-medium">
                            {formatPrice(trade.price)}
                          </span>
                          <div className="small text-muted">
                            {trade.base_is_seller ? t('trades.sell_order') : t('trades.buy_order')}
                          </div>
                        </td>
                        <td>
                          <div className="small">
                            <div className="mb-1">
                              <span className="text-muted">{t('trades.base')}:</span>{' '}
                              {trade.base_account ? (
                                <a 
                                  href={`/account/${trade.base_account}`}
                                  className="text-decoration-none"
                                  title={trade.base_account}
                                >
                                  <code className="small text-primary">
                                    {trade.base_account.slice(0, 6)}...{trade.base_account.slice(-4)}
                                  </code>
                                </a>
                              ) : (
                                <span className="text-muted small">N/A</span>
                              )}
                            </div>
                            <div>
                              <span className="text-muted">{t('trades.counter')}:</span>{' '}
                              {trade.counter_account ? (
                                <a 
                                  href={`/account/${trade.counter_account}`}
                                  className="text-decoration-none"
                                  title={trade.counter_account}
                                >
                                  <code className="small text-success">
                                    {trade.counter_account.slice(0, 6)}...{trade.counter_account.slice(-4)}
                                  </code>
                                </a>
                              ) : (
                                <span className="text-muted small">N/A</span>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            <Card.Footer className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                {t('trades.showing_results', { count: filteredTrades.length, total: tradesData._embedded.records.length })}
              </small>
              <div className="d-flex gap-2">
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  disabled={!tradesData._links.prev}
                  onClick={handlePrevPage}
                >
                  {FiChevronLeft({size:16,className:'me-1'})}
                  {t('trades.previous')}
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  disabled={!tradesData._links.next}
                  onClick={handleNextPage}
                >
                  {t('trades.next')}
                  {FiChevronRight({size:16,className:'ms-1'})}
                </Button>
              </div>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Trades;
